import React from "react";
import Reveal from "../common/Reveal";

const YOUTUBE_SHORTS_ID = "VUgLPSYWmuM";

const StudentTestimonialVideo: React.FC = () => {
  return (
    <section
      id="student-voices"
      className="relative overflow-hidden bg-primary py-20 sm:py-28"
    >
      {/* Subtle radial glow behind the video */}
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden="true"
      >
        <div className="absolute left-1/2 top-1/2 h-[600px] w-[600px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-accentGold/8 blur-[120px]" />
      </div>

      <div className="relative mx-auto flex max-w-6xl flex-col items-center gap-14 px-6 lg:flex-row lg:items-center lg:gap-20">

        {/* ── Left: copy ── */}
        <Reveal className="flex-1 space-y-6 text-center lg:text-left">
          <p className="text-xs uppercase tracking-[0.35em] text-accentNavy/45">
            Hear it from them
          </p>

          <h2 className="font-display text-3xl text-accentNavy sm:text-4xl lg:text-5xl">
            Real students.
            <br />
            <span className="text-accentGold">Real progress.</span>
          </h2>

          <p className="text-base text-accentNavy/70 lg:text-lg">
            Nothing speaks louder than seeing someone just like you pick up
            the ukulele and actually fall in love with it. These are a few of
            our students sharing their honest experience — unscripted.
          </p>

          {/* Decorative stat pills */}
          <div className="flex flex-wrap justify-center gap-4 lg:justify-start">
            {[
              { value: "1140+", label: "Students taught" },
              { value: "3 mo.", label: "Avg. time to first song" },
            ].map((stat) => (
              <div
                key={stat.label}
                className="rounded-2xl border border-accentNavy/10 bg-white/80 px-5 py-3 shadow-[0_8px_24px_-12px_rgba(9,32,63,0.18)] backdrop-blur-sm"
              >
                <p className="text-lg font-bold text-accentNavy">{stat.value}</p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-accentNavy/50">
                  {stat.label}
                </p>
              </div>
            ))}
          </div>
        </Reveal>

        {/* ── Right: phone-frame video ── */}
        <Reveal delay={150} className="flex flex-shrink-0 justify-center">
          {/* Phone shell */}
          <div className="relative">
            {/* Outer glow ring */}
            <div className="absolute -inset-3 rounded-[44px] bg-accentGold/15 blur-xl" />

            {/* Phone body */}
            <div className="relative overflow-hidden rounded-[40px] border-[3px] border-accentNavy/20 bg-accentNavy shadow-[0_32px_80px_-20px_rgba(9,32,63,0.65)]"
              style={{ width: "260px" }}
            >
              {/* Notch */}
              <div className="relative flex h-8 items-center justify-center bg-accentNavy">
                <div className="h-2.5 w-16 rounded-full bg-black/60" />
              </div>

              {/* 9:16 video */}
              <div className="relative w-full overflow-hidden bg-black" style={{ aspectRatio: "9/16" }}>
                <iframe
                  src={`https://www.youtube.com/embed/${YOUTUBE_SHORTS_ID}?rel=0&modestbranding=1&playsinline=1`}
                  title="Student testimonial compilation"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute inset-0 h-full w-full"
                />
              </div>

              {/* Bottom chin */}
              <div className="flex h-8 items-center justify-center bg-accentNavy">
                <div className="h-1 w-10 rounded-full bg-white/20" />
              </div>
            </div>

            {/* Floating badge */}
            <div className="absolute -right-4 -top-3 flex items-center gap-1.5 rounded-full border border-accentNavy/10 bg-white/95 px-3.5 py-2 shadow-[0_8px_24px_-8px_rgba(9,32,63,0.25)] backdrop-blur-sm">
              <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
              <span className="text-[11px] font-semibold uppercase tracking-[0.18em] text-accentNavy/80">
                Live stories
              </span>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default StudentTestimonialVideo;
